// SAD-1005 slice 2: Your Areas and How It Is Trending, worded from the one
// summary the page reads (GetPortfolioDeliverySummary, the Command Center's
// read for the same scope). Value, target and tone are the server's reading
// (SAD-998) and render as given; this module only words them and says where
// each tile hands off.
import { EMPTY_FILTER } from '../Delivery/CommandCenter/filters';
import { listHrefFor } from '../Delivery/CommandCenter/listView';
import { toneOf, valueText } from '../Delivery/CommandCenter/readings';
export function parseScope(params) {
    return params.get('scope') === 'all' ? 'all' : 'mine';
}
export const ccScope = (s)=>s === 'all' ? 'portfolio' : 'mine';
const plural = (n, one, many = `${one}s`)=>`${n.toLocaleString()} ${n === 1 ? one : many}`;
/** "your 3 programs" for the viewer's own; "7 programs" otherwise. */ export function programsWords(programs, own) {
    return own ? `your ${plural(programs, 'program')}` : plural(programs, 'program');
}
/** Whether the tiles read the viewer's own programs (My programs, not fallen back). */ export const isOwn = (scope, s)=>scope === 'mine' && !!s && !s.mineFallback;
/** The program with the most late dates, when it is not the only one. */ function topLate(s) {
    if (s.programs.length < 2) return null;
    const top = [
        ...s.programs
    ].sort((a, b)=>(b.counts?.stateCounts.late ?? 0) - (a.counts?.stateCounts.late ?? 0))[0];
    const late = top?.counts?.stateCounts.late ?? 0;
    return top && late > 0 ? {
        name: top.name,
        late
    } : null;
}
/**
 * The area tiles in priority order (design §3.4), those that apply: on-time
 * delivery and late now with any program in scope; waiting on approval with
 * Everything, or with programs of the viewer's own.
 */ export function deliveryTiles(s, scope) {
    if (s.programs.length === 0) return [];
    const own = isOwn(scope, s);
    const context = `Delivery · ${programsWords(s.programs.length, own)}`;
    const base = {
        ...EMPTY_FILTER,
        scope: ccScope(scope)
    };
    const byId = new Map(s.kpis.map((k)=>[
            k.id,
            k
        ]));
    const tile = (k, rest)=>({
            id: k.id,
            label: k.label,
            value: valueText(k),
            muted: !k.hasValue,
            tone: toneOf(k),
            kpiTone: k.tone,
            info: k.rule,
            context,
            ...rest
        });
    const out = [];
    const onTime = byId.get('on_time');
    if (onTime) {
        out.push(tile(onTime, {
            note: [
                `Target: ${onTime.target}`,
                onTime.hasValue ? `${onTime.numerator.toLocaleString()} of ${onTime.denominator.toLocaleString()}` : ''
            ].filter(Boolean).join(' · '),
            trend: s.trend.map((m)=>m.reading?.hasValue ? m.reading.value : null),
            href: `/delivery/command-center${scope === 'all' ? '?scope=portfolio' : ''}`,
            action: 'Command Center →'
        }));
    }
    const late = byId.get('late_now');
    if (late) {
        const top = topLate(s);
        out.push(tile(late, {
            note: [
                `Target: ${late.target}`,
                top ? `${top.late.toLocaleString()} on ${top.name}` : ''
            ].filter(Boolean).join(' · '),
            trend: s.trend.map((m)=>m.lateAtMonthEnd),
            href: listHrefFor({
                ...base,
                states: [
                    'late'
                ]
            }),
            action: 'Late deliverables →'
        }));
    }
    const waiting = byId.get('waiting_on_approval');
    if (waiting && (scope === 'all' || own)) {
        out.push(tile(waiting, {
            note: [
                waiting.detail,
                `Target: ${waiting.target}`
            ].filter(Boolean).join(' · '),
            href: listHrefFor({
                ...base,
                states: [
                    'awaiting_review'
                ]
            }),
            action: 'Waiting list →'
        }));
    }
    return out;
}
/** The trend chart's one sentence: the latest move, and where the late dates are. */ export function trendInsight(s) {
    const known = s.trend.filter((m)=>m.reading?.hasValue);
    const parts = [];
    if (known.length >= 2) {
        const last = known[known.length - 1];
        const prev = known[known.length - 2];
        const d = (last.reading?.value ?? 0) - (prev.reading?.value ?? 0);
        const month = new Date(`${prev.month}-01T00:00:00Z`).toLocaleDateString('en-US', {
            month: 'long',
            timeZone: 'UTC'
        });
        parts.push(d === 0 ? `Level with ${month}.` : `${d > 0 ? 'Up' : 'Down'} ${plural(Math.abs(d), 'point')} since ${month}.`);
    }
    const late = s.totals?.stateCounts.late ?? 0;
    const top = topLate(s);
    if (top && late > 0) {
        parts.push(top.late === late ? late === 1 ? `The late deliverable is on ${top.name}.` : `All ${plural(late, 'late deliverable')} are on ${top.name}.` : `${top.name} holds ${top.late.toLocaleString()} of the ${plural(late, 'late deliverable')}.`);
    }
    return parts.join(' ');
}
/** "$12M", "$1.5B", "$500K": a whole-dollar sum, short. */ export function moneyShort(dollars) {
    const n = Number(dollars);
    const fmt = (v, unit)=>`$${v >= 100 ? Math.round(v) : Number(v.toFixed(1)).toLocaleString()}${unit}`;
    if (n >= 1e9) return fmt(n / 1e9, 'B');
    if (n >= 1e6) return fmt(n / 1e6, 'M');
    if (n >= 1e3) return fmt(n / 1e3, 'K');
    return `$${n.toLocaleString()}`;
}
/** The pursuits counted, in words ("your 2 pursuits", "12 pursuits"). */ export function pursuitsWords(p, scope) {
    const own = scope === 'mine' && !p.mineFallback;
    return own ? `your ${plural(p.pursuits, 'pursuit')}` : plural(p.pursuits, 'pursuit');
}
/** The Decisions due tile, with any visible pursuit (design §3.4). */ export function pursuitTile(p) {
    const k = p?.decisionsDue;
    if (!p?.hasAccess || p.pursuits === 0 || !k) return null;
    return {
        id: 'decisions_due',
        label: k.label,
        value: valueText(k),
        muted: !k.hasValue,
        tone: toneOf(k),
        kpiTone: k.tone,
        info: k.rule,
        context: 'Pursuits · next 14 days',
        note: [
            k.detail,
            `Target: ${k.target}`
        ].filter(Boolean).join(' · '),
        href: '/capture',
        action: 'Pursuits →'
    };
}
/** Pursuits by stage: the open stages, then the closed outcomes of the last 12 months. */ export function pursuitBars(p) {
    return p.stages.map((s)=>({
            id: s.stage,
            label: s.label,
            open: s.open,
            count: s.count,
            end: s.valueCount > 0 ? `${s.count.toLocaleString()} · ${moneyShort(s.valueTotal)}` : s.count.toLocaleString(),
            // GRW-14: the Pursuits list filters by lifecycle, never by raw stage.
            href: s.open ? '/capture?lifecycle=live' : '/capture?lifecycle=closed'
        }));
}
/** The chart's one sentence: what is open, where most of it is, and the value on record. */ export function pursuitInsight(p) {
    if (p.openCount === 0) return 'No pursuit is open.';
    const top = [
        ...p.stages
    ].filter((s)=>s.open).sort((a, b)=>b.count - a.count)[0];
    const parts = [
        `${p.openCount.toLocaleString()} open`
    ];
    if (top && top.count > 0) parts.push(`${top.count.toLocaleString()} in ${top.label}`);
    let s = `${parts.join(', ')}.`;
    s += p.openValueCount > 0 ? ` Value is on record for ${p.openValueCount.toLocaleString()} of ${p.openCount.toLocaleString()}: ${moneyShort(p.openValueTotal)}.` : ' No open pursuit has a value on record.';
    return s;
}
/** The New matches tile, with any tracking profile (design §3.4). */ export function matchesTile(m) {
    if (!m?.profiles) return null;
    const k = {
        tone: m.tone
    };
    return {
        id: 'new_matches',
        label: 'New matches',
        value: m.hasValue ? m.newMatches.toLocaleString() : 'No data yet',
        muted: !m.hasValue,
        tone: toneOf(k),
        kpiTone: m.tone,
        info: m.rule,
        context: `Opportunities · ${plural(m.profiles, 'profile')}`,
        note: [
            m.detail,
            `Target: ${m.target}`
        ].filter(Boolean).join(' · '),
        href: '/opportunities',
        action: 'Opportunities →'
    };
}
